import React, { useEffect, useRef, useState } from "react";
import { X, Eraser, Check, Send, Mail, Link as LinkIcon, Copy, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EmailPreviewBlock } from "@/components/email/EmailI18n";

/**
 * Modal "Firma del cliente".
 *
 * Props:
 * - open: bool
 * - onClose: () => void
 * - repair: objeto repair (con id, code, signature, signature_status, customer_id)
 * - customerEmail: string
 * - onSigned: (updatedRepair) => void   // notifica al padre con la reparación actualizada
 */
export default function SignatureModal({ open, onClose, repair, customerEmail, onSigned }) {
  const [tab, setTab] = useState("presential");
  const [emailTo, setEmailTo] = useState(customerEmail || "");
  const [emailLang, setEmailLang] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (open) {
      setEmailTo(customerEmail || "");
      setEmailLang("");
      setLinkData(null);
      setSigned(
        repair?.signature_status === "signed_presential" ||
          repair?.signature_status === "signed_remote" ||
          repair?.signature_status === "discarded_company"
      );
      setTab("presential");
      // Iter 114 — Al abrir el modal, buscar si YA existe un enlace activo
      // para esta reparación y cargarlo (idempotente, no crea uno nuevo).
      if (repair?.id) {
        api.get(`/repairs/${repair.id}/signature/current-link`)
          .then((r) => {
            if (r.data?.has_active_link && r.data.link) {
              setLinkData({
                url: r.data.link.url,
                expires_at: r.data.link.expires_at,
                token: r.data.link.token,
                ttl_days: null,
                reused: true,
              });
            }
          })
          .catch(() => { /* noop */ });
      }
    }
  }, [open, customerEmail, repair?.id, repair?.signature_status]);

  if (!open) return null;

  const handleSigned = (updatedRepair) => {
    setSigned(true);
    if (updatedRepair && onSigned) onSigned(updatedRepair);
  };

  const replaceSignature = async () => {
    const confirmed = window.confirm(
      "Esta reparación ya dispone de una firma. ¿Desea reemplazarla?\n\n• Aceptar = SÍ, borro la firma actual y capturo una nueva\n• Cancelar = mantener la firma actual"
    );
    if (!confirmed) return;
    try {
      const r = await api.delete(`/repairs/${repair.id}/signature`);
      toast.success("Firma anterior eliminada. Captura la nueva.");
      if (onSigned) onSigned(r.data);
      setSigned(false);
      setTab("presential");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar la firma anterior");
    }
  };

  const discardCompany = async () => {
    const confirmed = window.confirm(
      "¿Marcar como cliente empresa y descartar la firma?\n\nNo se volverá a pedir firma para esta reparación."
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repair.id}/signature/discard-company`);
      toast.success("Firma descartada por cliente empresa.");
      if (onSigned) onSigned(r.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descartar la firma");
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!emailTo || !emailTo.includes("@")) {
      toast.error("Indica un email válido.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/repairs/${repair.id}/send-confirmation-email`, { to_email: emailTo, language_code: emailLang || null });
      toast.success(`Email enviado a ${emailTo}`);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el email");
    } finally {
      setBusy(false);
    }
  };

  const awaitRemote = async () => {
    setBusy(true);
    try {
      const customerPhone = repair?.customer_phone || "";
      const channel = emailTo && customerPhone ? "both" : (emailTo ? "email" : (customerPhone ? "whatsapp" : "email"));
      const r = await api.post(`/repairs/${repair.id}/signature/await-remote`, {
        channel,
        to_email: emailTo || undefined,
        to_phone: customerPhone || undefined,
        language_code: emailLang || undefined,
      });
      const data = r.data || {};
      const sent = data.channels_sent || {};
      const parts = [];
      if (sent.email) parts.push("email");
      if (sent.whatsapp) parts.push("WhatsApp");
      const reusedNote = data.reused ? " (enlace reutilizado)" : "";
      toast.success(
        parts.length
          ? `Enlace enviado por ${parts.join(" + ")}${reusedNote}. La reparación queda en espera de firma remota.`
          : `Enlace preparado${reusedNote}.`
      );
      // Iter 114 — Guardar el enlace en el modal para que "Copiar" use el mismo.
      if (data.url) {
        let absoluteUrl = data.url;
        if (absoluteUrl.startsWith("/")) absoluteUrl = `${window.location.origin}${absoluteUrl}`;
        setLinkData({
          url: absoluteUrl,
          expires_at: data.expires_at,
          token: data.token,
          ttl_days: data.ttl_days,
          reused: data.reused,
        });
      }
      if (sent.whatsapp && data.wa_link) window.open(data.wa_link, "_blank");
      // Refrescar reparación si el padre lo pide (para actualizar el status).
      if (onSigned) {
        try {
          const fresh = await api.get(`/repairs/${repair.id}`);
          onSigned(fresh.data);
        } catch { /* noop */ }
      }
      // No cerramos el modal automáticamente: el usuario puede querer copiar
      // el enlace o revocarlo.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el enlace");
    } finally {
      setBusy(false);
    }
  };

  const revokeLink = async () => {
    const confirmed = window.confirm(
      "¿Revocar el enlace de firma remota actual?\n\nEl cliente ya no podrá firmar con el enlace enviado. Podrás generar uno nuevo después."
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repair.id}/signature/revoke-links`);
      const revoked = r?.data?.revoked || 0;
      toast.success(revoked > 0 ? `Enlace revocado (${revoked}).` : "No había enlaces activos que revocar.");
      setLinkData(null);
      if (onSigned) {
        try {
          const fresh = await api.get(`/repairs/${repair.id}`);
          onSigned(fresh.data);
        } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revocar el enlace");
    } finally {
      setBusy(false);
    }
  };

  const generateLink = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repair.id}/signature-link`);
      const data = r.data || {};
      if (data.url && data.url.startsWith("/")) {
        data.url = `${window.location.origin}${data.url}`;
      }
      setLinkData(data);
      toast.success(data.reused ? "Se ha reutilizado el enlace activo existente." : "Enlace generado correctamente.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el enlace");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkData?.url || "");
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="signature-modal"
    >
      <div className="ds-card w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              {repair?.code || "—"}
            </div>
            <h2 className="text-lg font-bold text-[#111110]">Firma del cliente</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F7F7F4]"
            data-testid="signature-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs (solo si NO está firmado) */}
        {!signed && (
          <div className="flex items-center border-b border-[#E5E5E0]">
            <TabBtn active={tab === "presential"} onClick={() => setTab("presential")} testid="tab-presential">
              Firma presencial
            </TabBtn>
            <TabBtn active={tab === "remote"} onClick={() => setTab("remote")} testid="tab-remote">
              Firma remota
            </TabBtn>
            <div className="ml-auto pr-3">
              <button
                onClick={discardCompany}
                disabled={busy}
                className="text-xs px-3 py-1.5 border border-[#E5E5E0] hover:border-[#111110] hover:bg-[#F7F7F4] flex items-center gap-1.5 disabled:opacity-50"
                data-testid="signature-discard-company"
                title="Marcar como cliente empresa. No se volverá a pedir firma."
              >
                <Briefcase size={12} /> Cliente empresa
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-5">
          {signed ? (
            <SignedSummary repair={repair} onReplace={replaceSignature} />
          ) : tab === "presential" ? (
            <PresentialSignaturePad repair={repair} onSigned={handleSigned} />
          ) : (
            <RemoteSignatureBlock
              repair={repair}
              linkData={linkData}
              onGenerate={generateLink}
              onAwait={awaitRemote}
              onCopy={copyLink}
              onRevoke={revokeLink}
              busy={busy}
              awaitingRemote={repair?.signature_status === "awaiting_remote"}
              previewBlock={emailTo ? (
                <EmailPreviewBlock templateKey="customer_signature_request" repairId={repair?.id} language={emailLang} onLanguage={(v) => setEmailLang(v)} />
              ) : null}
            />
          )}
        </div>

        {/* Footer email actions (solo cuando ya está firmado) */}
        {signed && (
          <div className="px-5 py-4 border-t border-[#E5E5E0] bg-[#FAFAF8] space-y-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
                Email del cliente
              </label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="cliente@email.com"
                className="w-full ds-card px-3 py-2 text-sm focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none"
                data-testid="signature-email-input"
              />
            </div>
            <EmailPreviewBlock templateKey="repair_signed" repairId={repair?.id} language={emailLang} onLanguage={(v) => setEmailLang(v)} />
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 border border-[#E5E5E0] text-sm hover:bg-white"
                data-testid="signature-save-no-email"
              >
                Guardar sin enviar email
              </button>
              <button
                onClick={sendEmail}
                disabled={busy}
                className="px-4 py-2 bg-[#111110] text-white text-sm hover:bg-[#000] flex items-center gap-2"
                data-testid="signature-send-email"
              >
                <Mail size={14} /> {busy ? "Enviando..." : "Enviar email al cliente"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? "bg-white border-b-2 border-[#111110] text-[#111110]"
          : "bg-[#FAFAF8] text-[#525252] hover:text-[#111110]"
      }`}
    >
      {children}
    </button>
  );
}

function SignedSummary({ repair, onReplace }) {
  const sig = repair?.signature || {};
  const status = repair?.signature_status;
  const label =
    status === "signed_presential"
      ? "Firmado presencialmente"
      : status === "signed_remote"
      ? "Firmado remotamente"
      : status === "awaiting_remote"
      ? "Pendiente de firma remota"
      : status === "discarded_company"
      ? "Firma descartada · Cliente empresa"
      : "Pendiente";
  const [showFull, setShowFull] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Check size={16} className="text-[#6E8B3D]" />
        <span className="font-bold text-sm text-[#6E8B3D]" data-testid="signature-status-label">
          {label}
        </span>
      </div>
      {sig.image_b64 && (
        <div className="border border-[#E5E5E0] bg-white p-3 text-center relative">
          <img
            src={sig.image_b64}
            alt="Firma"
            className="max-w-full max-h-44 inline-block"
            data-testid="signature-image-preview"
          />
          <button
            onClick={() => setShowFull(true)}
            className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider border border-[#E5E5E0] bg-white px-2 py-0.5 hover:bg-[#F7F7F4]"
            data-testid="signature-view-full"
          >
            Ver firma
          </button>
        </div>
      )}
      <div className="text-xs text-[#525252] font-mono space-y-0.5">
        {sig.signed_at && <div>Fecha: {new Date(sig.signed_at).toLocaleString("es-ES")}</div>}
        {sig.signed_by_user_name && status === "signed_presential" && (
          <div>Capturada por: {sig.signed_by_user_name}</div>
        )}
        {sig.ip && <div>IP: {sig.ip}</div>}
        {sig.user_agent && <div className="truncate" title={sig.user_agent}>Navegador: {sig.user_agent}</div>}
      </div>
      <div className="pt-2 border-t border-[#E5E5E0]">
        <button
          onClick={onReplace}
          data-testid="signature-replace"
          className="text-[11px] font-mono uppercase tracking-wider text-[#525252] hover:text-red-700 hover:underline inline-flex items-center gap-1"
        >
          <Eraser size={11} /> {status === "discarded_company" ? "Reactivar petición de firma" : "Solicitar nueva firma"}
        </button>
      </div>
      {showFull && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowFull(false)}
          data-testid="signature-fullscreen"
        >
          <img src={sig.image_b64} alt="Firma" className="max-w-[90vw] max-h-[90vh] bg-white p-4" />
        </div>
      )}
    </div>
  );
}

function PresentialSignaturePad({ repair, onSigned }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Fix sharp drawing on HiDPI
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    ctx.strokeStyle = "#111110";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const stop = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.clientWidth, c.clientHeight);
    setHasInk(false);
  };

  const confirm = async () => {
    if (!hasInk) {
      toast.error("Firma vacía");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const r = await api.post(`/repairs/${repair.id}/signature`, { image_b64: dataUrl });
      toast.success("Firma guardada");
      onSigned && onSigned(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la firma");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#525252]">
        Firma con el ratón, trackpad, pantalla táctil o lápiz dentro del recuadro.
      </p>
      <div className="border-2 border-dashed border-[#E5E5E0] bg-white" data-testid="signature-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="w-full h-56 block touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={stop}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <button
          onClick={clear}
          disabled={saving}
          className="px-4 py-2 border border-[#E5E5E0] text-sm hover:bg-[#F7F7F4] flex items-center gap-2"
          data-testid="signature-clear"
        >
          <Eraser size={14} /> Borrar firma
        </button>
        <button
          onClick={confirm}
          disabled={saving || !hasInk}
          className="px-4 py-2 bg-[#6E8B3D] text-white text-sm hover:bg-[#5d7634] disabled:opacity-50 flex items-center gap-2"
          data-testid="signature-confirm"
        >
          <Check size={14} /> {saving ? "Guardando..." : "Confirmar firma"}
        </button>
      </div>
    </div>
  );
}

function RemoteSignatureBlock({ repair, linkData, onGenerate, onAwait, onCopy, onRevoke, busy, awaitingRemote, previewBlock }) {
  // Iter 114 — si ttl_days no viene (por reutilización), lo calculamos desde expires_at.
  let ttl = linkData?.ttl_days;
  if (!ttl && linkData?.expires_at) {
    const diffMs = new Date(linkData.expires_at).getTime() - Date.now();
    const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    ttl = days;
  }
  const hasLink = !!linkData?.url;
  return (
    <div className="space-y-3">
      {awaitingRemote && (
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="signature-awaiting-banner">
          ⏳ Esta reparación está <b>en espera de firma remota</b>. El cliente ya tiene el enlace.
        </div>
      )}
      <p className="text-sm text-[#111110]">
        Genera un enlace único para que el cliente firme desde su propio dispositivo (móvil, tablet
        u ordenador). El enlace caduca tras firmar o pasados {ttl || 15} días.
      </p>
      {previewBlock}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onAwait}
          disabled={busy}
          className="px-4 py-2 bg-[#6E8B3D] text-white text-sm hover:bg-[#5d7634] disabled:opacity-50 flex items-center gap-2"
          data-testid="signature-await-remote"
          title="Envía el enlace al cliente por email (reutiliza el enlace activo si existe)."
        >
          <Send size={14} /> {busy ? "Enviando..." : (hasLink ? "Reenviar enlace por email" : "Enviar enlace por email")}
        </button>
        {!hasLink && (
          <button
            onClick={onGenerate}
            disabled={busy}
            className="px-4 py-2 border border-[#111110] text-[#111110] text-sm hover:bg-[#F7F7F4] disabled:opacity-50 flex items-center gap-2"
            data-testid="signature-generate-link"
          >
            <LinkIcon size={14} /> {busy ? "Generando..." : "Solo generar enlace"}
          </button>
        )}
      </div>
      {hasLink && (
        <div className="space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block">
            Enlace de firma activo
          </label>
          <div className="flex items-stretch border border-[#E5E5E0] bg-white">
            <div className="flex-1 px-3 py-2 text-xs font-mono break-all" data-testid="signature-link-url">
              {linkData.url}
            </div>
            <button
              onClick={onCopy}
              className="px-3 border-l border-[#E5E5E0] hover:bg-[#F7F7F4]"
              title="Copiar enlace"
              data-testid="signature-link-copy"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-[#525252]">
              <LinkIcon size={11} className="inline mr-1" />
              Caduca: {linkData.expires_at ? new Date(linkData.expires_at).toLocaleString("es-ES") : "—"}
            </div>
            <button
              onClick={onRevoke}
              disabled={busy}
              className="px-3 py-1.5 border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="signature-link-revoke"
              title="Revocar el enlace actual"
            >
              Revocar enlace
            </button>
          </div>
          <p className="text-xs text-[#525252] italic">
            Este mismo enlace se reutiliza al pulsar &quot;Reenviar&quot; o &quot;Copiar&quot;. La reparación se marcará como
            firmada únicamente después de que el cliente confirme la firma.
          </p>
        </div>
      )}
    </div>
  );
}
